import { Info, PackagePlus, Sparkles, Upload } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Progress,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useChat,
  useToast,
} from "randmarcomps";
import type { InsertProductInput, Product } from "randmar-api-client";
import {
  useGetV4ManufacturerByRouteManufacturerIdAccountQuery,
  useLazyGetV4ManufacturerByRouteManufacturerIdProductAndProductNumberQuery,
  usePutV4ManufacturerByRouteManufacturerIdProductMutation,
} from "randmar-api-client";
import CategorySelector from "../components/products/CategorySelector";
import { DataRefreshBar } from "../components/util/DataRefreshBar";
import RichTextEditor from "../components/util/textEditor/RichTextEditor";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import withRoot from "../withRoot";

const BULK_UPLOAD_MANDATORY_FIELDS = [
  "UPC",
  "Manufacturer Part Number (MPN)",
  "Product Title",
  "Category",
  "Standard Cost",
  "Master Carton",
  "Unit Weight",
];

type StepId = "upc" | "integration" | "distribution";

const steps: Array<{ id: StepId; label: string; title: string; description: string }> = [
  { id: "upc", label: "UPC", title: "UPC", description: "Start with a UPC (optional)." },
  { id: "integration", label: "Integration", title: "Integration", description: "Add catalog details." },
  { id: "distribution", label: "Distribution", title: "Distribution", description: "Set pricing and shipping basics." },
];

const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== "object") return;
  if (!("status" in error)) return;
  const status = (error as { status: unknown }).status;
  return typeof status === "number" ? status : undefined;
};

const isValidMoneyInput = (raw: string) => /^[0-9]*([.][0-9]{0,2})?$/.test(raw);
const isValidIntegerInput = (raw: string) => /^[0-9]*$/.test(raw);

const InsertProductPage: React.FC = () => {
  const { applicationId } = useManufacturer();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { sendMessageWithClear } = useChat();

  const manufacturerAccountQuery = useGetV4ManufacturerByRouteManufacturerIdAccountQuery({
    routeManufacturerId: applicationId,
  });
  const manufacturerAccount = manufacturerAccountQuery.data;
  const currencyCode = manufacturerAccount?.CurrencyCode ?? "USD";
  const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);

  const [activeStep, setActiveStep] = React.useState<number>(0);
  const [furthestStep, setFurthestStep] = React.useState<number>(0);

  const [title, setTitle] = React.useState("");
  const [mpn, setMpn] = React.useState("");
  const [map, setMap] = React.useState("");
  const [msrp, setMsrp] = React.useState("");
  const [bodyHTML, setBodyHTML] = React.useState("");
  const [selectedCategory, setSelectedCategory] = React.useState("");

  const [cost, setCost] = React.useState("");
  const [unitWeight, setUnitWeight] = React.useState("");
  const [masterCarton, setMasterCarton] = React.useState("1");
  const [skidQuantity, setSkidQuantity] = React.useState("");
  const [upc, setUpc] = React.useState("");

  const [confirmInsertOpen, setConfirmInsertOpen] = React.useState(false);
  const [existingProductDialogOpen, setExistingProductDialogOpen] = React.useState(false);
  const [existingProduct, setExistingProduct] = React.useState<Product | undefined>();

  const [lookupProduct, { isFetching: isLookupBusy }] = useLazyGetV4ManufacturerByRouteManufacturerIdProductAndProductNumberQuery();
  const [insertProduct, { isLoading: isInsertBusy }] = usePutV4ManufacturerByRouteManufacturerIdProductMutation();

  const busy = isLookupBusy || isInsertBusy;
  const stepValue = steps[activeStep]?.id ?? "upc";
  const progressValue = (activeStep / Math.max(steps.length - 1, 1)) * 100;

  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = `Insert Product | ${previousTitle}`;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  React.useEffect(() => {
    if (manufacturerAccountQuery.isFetching) return;
    if (manufacturerAccountQuery.isSuccess || manufacturerAccountQuery.isError) {
      setLastRefreshedAt(new Date());
    }
  }, [manufacturerAccountQuery.isError, manufacturerAccountQuery.isFetching, manufacturerAccountQuery.isSuccess]);

  const upcIsValid = upc.length === 0 || (/^[0-9]{10,14}$/.test(upc) && !Number.isNaN(Number(upc)));
  const integrationIsValid = title.trim() !== "" && mpn.trim() !== "" && selectedCategory.trim() !== "";

  const insertProductAssistantPrompt = React.useMemo(() => {
    return [
      "You are assisting with Insert Product on the Randmar Manufacturer Portal.",
      `Manufacturer applicationId: ${applicationId}`,
      `Currency: ${currencyCode}`,
      "",
      "Current form values:",
      `- UPC: ${upc || "(blank)"}`,
      `- MPN: ${mpn || "(blank)"}`,
      `- Title: ${title || "(blank)"}`,
      `- Category: ${selectedCategory || "(blank)"}`,
      `- StandardCost: ${cost || "(blank)"}`,
      `- UnitWeight: ${unitWeight || "(blank)"}`,
      `- MasterCarton: ${masterCarton || "(blank)"}`,
      `- SkidQuantity: ${skidQuantity || "(blank)"}`,
      `- MAP: ${map || "(blank)"}`,
      `- MSRP: ${msrp || "(blank)"}`,
      `- BodyHTML: ${bodyHTML.trim() ? "(provided)" : "(blank)"}`,
      "",
      "Please help me:",
      "1) Validate required fields by step (UPC optional; Integration requires Category + MPN + Title; Distribution requires StandardCost + UnitWeight + MasterCarton).",
      "2) Suggest sensible defaults/formatting (money, integers, weights).",
      "3) If I paste values, map them to InsertProductInput JSON for PUT /V4/Manufacturer/{routeManufacturerId}/Product.",
    ].join("\n");
  }, [applicationId, bodyHTML, cost, currencyCode, map, masterCarton, mpn, msrp, selectedCategory, skidQuantity, title, unitWeight, upc]);

  const bulkUploadAssistantPrompt = React.useMemo(() => {
    const mandatoryFields = BULK_UPLOAD_MANDATORY_FIELDS.map((field) => `- ${field}`).join("\n");

    return [
      "You are assisting with Product Bulk Upload on the Randmar Manufacturer Portal Insert Product page.",
      `Manufacturer applicationId: ${applicationId}`,
      `Currency: ${currencyCode}`,
      "",
      "Goal: help me prepare an .xlsx spreadsheet for bulk product insertion.",
      "",
      "Mandatory spreadsheet fields:",
      mandatoryFields,
      "",
      "The API supports these InsertProductInput properties:",
      "- UPC (string, optional)",
      "- Category (string, optional)",
      "- MPN (string, optional)",
      "- Title (string, optional)",
      "- StandardCost (number, optional)",
      "- UnitWeight (number, optional)",
      "- MasterCarton (integer, optional)",
      "- SkidQuantity (integer, optional)",
      "- MAP (number, optional)",
      "- MSRP (number, optional)",
      "- BodyHTML (string, optional)",
      "",
      "Please:",
      "1) Suggest exact column headers and an example row.",
      "2) Call out common data-cleaning issues (e.g., UPC numeric-only, money formatting, integer vs decimal).",
      "3) If I paste rows, validate them and map them to InsertProductInput JSON for PUT /V4/Manufacturer/{routeManufacturerId}/Product.",
    ].join("\n");
  }, [applicationId, currencyCode]);

  const parsedCost = cost === "" ? undefined : Number(cost);
  const parsedUnitWeight = unitWeight === "" ? undefined : Number(unitWeight);
  const parsedMasterCarton = masterCarton === "" ? undefined : Number(masterCarton);
  const distributionIsValid =
    integrationIsValid &&
    typeof parsedCost === "number" &&
    parsedCost > 0 &&
    typeof parsedUnitWeight === "number" &&
    parsedUnitWeight > 0 &&
    typeof parsedMasterCarton === "number" &&
    parsedMasterCarton > 0;

  const goToStep = React.useCallback(
    (targetStep: number) => {
      if (targetStep > furthestStep) return;
      setActiveStep(targetStep);
    },
    [furthestStep]
  );

  const goNext = React.useCallback(() => {
    setActiveStep((prev) => {
      const nextStep = Math.min(prev + 1, steps.length - 1);
      setFurthestStep((furthest) => Math.max(furthest, nextStep));
      return nextStep;
    });
  }, []);

  const goBack = React.useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleExistingProductNavigate = React.useCallback(() => {
    const sku = existingProduct?.RandmarSKU;
    if (!sku) return;
    navigate(Path.product(applicationId, sku));
  }, [applicationId, existingProduct?.RandmarSKU, navigate]);

  const checkProductExists = React.useCallback(
    async (productNumber: string) => {
      try {
        const product = await lookupProduct({
          routeManufacturerId: applicationId,
          productNumber,
          withSpecification: false,
        }).unwrap();
        return product;
      } catch (error) {
        const status = getErrorStatus(error);
        if (status === 404) return undefined;
        throw error;
      }
    },
    [applicationId, lookupProduct]
  );

  const handleUPCContinue = React.useCallback(async () => {
    if (!upcIsValid) return;
    if (!upc) {
      goNext();
      return;
    }

    try {
      const product = await checkProductExists(upc);
      if (product) {
        setExistingProduct(product);
        setExistingProductDialogOpen(true);
        return;
      }
      goNext();
    } catch {
      toast({ variant: "destructive", title: "Could not validate UPC", description: "Please try again." });
    }
  }, [checkProductExists, goNext, toast, upc, upcIsValid]);

  const handleIntegrationContinue = React.useCallback(async () => {
    if (!integrationIsValid) return;

    try {
      const product = await checkProductExists(mpn.trim());
      if (product?.IsManufacturer) {
        toast({ variant: "destructive", title: "MPN already exists", description: `Product ${mpn} is already in your catalog.` });
        return;
      }
      goNext();
    } catch {
      toast({ variant: "destructive", title: "Could not validate MPN", description: "Please try again." });
    }
  }, [checkProductExists, goNext, integrationIsValid, mpn, toast]);

  const handleConfirmInsert = React.useCallback(async () => {
    const insertProductInput: InsertProductInput = {
      UPC: upc.trim() ? upc.trim() : undefined,
      Category: selectedCategory.trim() ? selectedCategory.trim() : undefined,
      Title: title.trim() ? title.trim() : undefined,
      MPN: mpn.trim() ? mpn.trim() : undefined,
      StandardCost: cost.trim() ? Number(cost) : undefined,
      MasterCarton: masterCarton.trim() ? Number(masterCarton) : undefined,
      UnitWeight: unitWeight.trim() ? Number(unitWeight) : undefined,
      MAP: map.trim() ? Number(map) : undefined,
      MSRP: msrp.trim() ? Number(msrp) : undefined,
      BodyHTML: bodyHTML.trim() ? bodyHTML : undefined,
      SkidQuantity: skidQuantity.trim() ? Number(skidQuantity) : undefined,
    };

    try {
      const created = await insertProduct({ routeManufacturerId: applicationId, insertProductInput }).unwrap();
      const sku = created?.RandmarSKU;
      toast({ variant: "success", title: "Product inserted", description: sku ? `SKU ${sku} created.` : "Your product was created." });
      setConfirmInsertOpen(false);
      if (sku) {
        navigate(Path.product(applicationId, sku));
      }
    } catch {
      toast({ variant: "destructive", title: "Insert failed", description: "Could not create this product. Please review your inputs and try again." });
    }
  }, [applicationId, bodyHTML, cost, insertProduct, map, masterCarton, mpn, msrp, navigate, selectedCategory, skidQuantity, title, toast, unitWeight, upc]);

  return (
    <main className="mx-auto w-full max-w-[1440px] space-y-8 pb-10" aria-busy={busy}>
      <header className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm" role="banner">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-[var(--ops-accent-muted)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[color:var(--ops-accent)]">
              <PackagePlus className="h-4 w-4" aria-hidden="true" />
              Products
            </span>
            <h1 className="text-[32px] font-semibold tracking-tight text-gray-800">Insert Product</h1>
            <p className="text-[15px] text-gray-600">Add a SKU to your catalog in three steps.</p>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            <DataRefreshBar
              align="right"
              lastRefreshedAt={lastRefreshedAt}
              isRefreshing={manufacturerAccountQuery.isFetching}
              onRefresh={() => manufacturerAccountQuery.refetch()}
              actions={(
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => sendMessageWithClear(insertProductAssistantPrompt)}
                  className="rounded-xl"
                  aria-label="Open assistant with this page context"
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            />
          </div>
        </div>
      </header>

      <Card className="rounded-2xl border border-gray-100 bg-white shadow-sm">
        <CardHeader className="gap-2 border-b border-gray-100">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-[20px] font-medium text-gray-800">Manual Insert</CardTitle>
              <CardDescription className="text-[15px] text-gray-600">Create a product one step at a time.</CardDescription>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
              Step {activeStep + 1} / {steps.length}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 p-6">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-[15px] font-medium text-gray-700">{steps[activeStep]?.title}</p>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{steps[activeStep]?.description}</p>
            </div>
            <Progress value={progressValue} className="h-2" />
          </div>

          <Tabs
            value={stepValue}
            onValueChange={(value) => {
              const nextIndex = steps.findIndex((step) => step.id === value);
              if (nextIndex >= 0) {
                goToStep(nextIndex);
              }
            }}
          >
            <TabsList className="ops-tablist w-full">
              {steps.map((step, index) => (
                <TabsTrigger key={step.id} value={step.id} disabled={index > furthestStep || busy} className="ops-tab-trigger">
                  {step.label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="upc" className="mt-6">
              <Card className="rounded-2xl border border-gray-100 bg-white shadow-sm">
                <CardHeader className="gap-1">
                  <CardTitle className="text-[20px] font-medium text-gray-800">UPC</CardTitle>
                  <CardDescription className="text-[15px] text-gray-600">Optional, but recommended when available.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <Alert className="rounded-xl border border-[var(--ops-border)] bg-[var(--ops-accent-muted)]/40">
                    <Info className="h-4 w-4 text-[color:var(--ops-accent)]" aria-hidden="true" />
                    <AlertTitle className="text-gray-800">What&apos;s a UPC?</AlertTitle>
                    <AlertDescription className="text-[15px] text-gray-700">
                      A UPC is typically a 12-digit universal product code. If you don&apos;t have one, you can skip this step
                      and use the MPN instead.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <Label htmlFor="insert-product-upc" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      UPC (optional)
                    </Label>
                    <Input
                      id="insert-product-upc"
                      value={upc}
                      onChange={(event) => {
                        const next = event.target.value.trim();
                        if (next === "" || isValidIntegerInput(next)) setUpc(next);
                      }}
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="10–14 digits"
                      className="h-11 w-full max-w-sm rounded-lg border border-gray-200 bg-white px-3 text-[15px] text-gray-800 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                      disabled={busy}
                      aria-invalid={!upcIsValid}
                    />
                    {!upcIsValid && (
                      <p className="text-[13px] text-rose-600">Enter 10–14 digits (numbers only), or leave blank to skip.</p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={goNext} disabled={busy}>
                      Skip
                    </Button>
                    <Button type="button" className="w-full sm:w-auto" onClick={handleUPCContinue} disabled={busy || !upcIsValid}>
                      Continue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="integration" className="mt-6">
              <Card className="rounded-2xl border border-gray-100 bg-white shadow-sm">
                <CardHeader className="gap-1">
                  <CardTitle className="text-[20px] font-medium text-gray-800">Integration</CardTitle>
                  <CardDescription className="text-[15px] text-gray-600">Add the catalog info that resellers see.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="insert-product-mpn" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Manufacturer part number (required)
                    </Label>
                    <Input
                      id="insert-product-mpn"
                      value={mpn}
                      onChange={(event) => setMpn(event.target.value)}
                      autoComplete="off"
                      className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-[15px] text-gray-800 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                      disabled={busy}
                    />
                  </div>

                  <CategorySelector
                    disabled={busy}
                    selectedCategory={selectedCategory}
                    onSelectedCategoryChange={(newCategory: string) => setSelectedCategory(newCategory)}
                  />

                  <div className="space-y-2">
                    <Label htmlFor="insert-product-title" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Product title (required)
                    </Label>
                    <Input
                      id="insert-product-title"
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      autoComplete="off"
                      className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-[15px] text-gray-800 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                      disabled={busy}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="insert-product-map" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      MAP (optional)
                    </Label>
                    <div className="relative max-w-sm">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        $
                      </span>
                      <Input
                        id="insert-product-map"
                        value={map}
                        onChange={(event) => {
                          const next = event.target.value;
                          if (next === "" || isValidMoneyInput(next)) setMap(next);
                        }}
                        inputMode="decimal"
                        autoComplete="off"
                        className="h-11 w-full rounded-lg border border-gray-200 bg-white py-2 pl-7 pr-3 text-[15px] text-gray-800 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                        disabled={busy}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Description (optional)</p>
                    <RichTextEditor initialContent={bodyHTML} setContent={(newValue: string) => setBodyHTML(newValue)} />
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
                    <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={goBack} disabled={busy}>
                      Back
                    </Button>
                    <Button type="button" className="w-full sm:w-auto" onClick={handleIntegrationContinue} disabled={busy || !integrationIsValid}>
                      Continue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="distribution" className="mt-6">
              <Card className="rounded-2xl border border-gray-100 bg-white shadow-sm">
                <CardHeader className="gap-1">
                  <CardTitle className="text-[20px] font-medium text-gray-800">Distribution</CardTitle>
                  <CardDescription className="text-[15px] text-gray-600">Set cost, packaging, and optional MSRP.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="insert-product-cost" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Standard cost ({currencyCode}) (required)
                    </Label>
                    <div className="relative max-w-sm">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        $
                      </span>
                      <Input
                        id="insert-product-cost"
                        value={cost}
                        onChange={(event) => {
                          const next = event.target.value;
                          if (next === "" || isValidMoneyInput(next)) setCost(next);
                        }}
                        inputMode="decimal"
                        autoComplete="off"
                        className="h-11 w-full rounded-lg border border-gray-200 bg-white py-2 pl-7 pr-3 text-[15px] text-gray-800 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                        disabled={busy}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="insert-product-unit-weight" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Unit weight (lbs) (required)
                    </Label>
                    <div className="max-w-sm">
                      <Input
                        id="insert-product-unit-weight"
                        value={unitWeight}
                        onChange={(event) => {
                          const next = event.target.value;
                          if (next === "" || isValidMoneyInput(next)) setUnitWeight(next);
                        }}
                        inputMode="decimal"
                        autoComplete="off"
                        className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-[15px] text-gray-800 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                        disabled={busy}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="insert-product-master-carton" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Master carton (required)
                    </Label>
                    <div className="max-w-sm">
                      <Input
                        id="insert-product-master-carton"
                        value={masterCarton}
                        onChange={(event) => {
                          const next = event.target.value.trim();
                          if (next === "" || isValidIntegerInput(next)) setMasterCarton(next);
                        }}
                        inputMode="numeric"
                        autoComplete="off"
                        className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-[15px] text-gray-800 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                        disabled={busy}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="insert-product-skid-quantity" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Skid quantity (optional)
                    </Label>
                    <div className="max-w-sm">
                      <Input
                        id="insert-product-skid-quantity"
                        value={skidQuantity}
                        onChange={(event) => {
                          const next = event.target.value.trim();
                          if (next === "" || isValidIntegerInput(next)) setSkidQuantity(next);
                        }}
                        inputMode="numeric"
                        autoComplete="off"
                        className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-[15px] text-gray-800 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                        disabled={busy}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="insert-product-msrp" className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      MSRP ({currencyCode}) (optional)
                    </Label>
                    <div className="relative max-w-sm">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                        $
                      </span>
                      <Input
                        id="insert-product-msrp"
                        value={msrp}
                        onChange={(event) => {
                          const next = event.target.value;
                          if (next === "" || isValidMoneyInput(next)) setMsrp(next);
                        }}
                        inputMode="decimal"
                        autoComplete="off"
                        className="h-11 w-full rounded-lg border border-gray-200 bg-white py-2 pl-7 pr-3 text-[15px] text-gray-800 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                        disabled={busy}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
                    <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={goBack} disabled={busy}>
                      Back
                    </Button>
                    <Button
                      type="button"
                      className="w-full sm:w-auto"
                      onClick={() => setConfirmInsertOpen(true)}
                      disabled={busy || !distributionIsValid}
                    >
                      Insert product
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border border-gray-100 bg-white shadow-sm">
        <CardHeader className="gap-2 border-b border-gray-100">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle className="text-[20px] font-medium text-gray-800">Product Bulk Upload</CardTitle>
              <CardDescription className="text-[15px] text-gray-600">Send us your spreadsheet and we&apos;ll insert products for you.</CardDescription>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
              <Upload className="h-4 w-4 text-[color:var(--ops-accent)]" aria-hidden="true" />
              XLSX
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 p-6">
          <Alert className="rounded-xl border border-[var(--ops-border)] bg-[var(--ops-accent-muted)]/40">
            <Sparkles className="h-4 w-4 text-[color:var(--ops-accent)]" aria-hidden="true" />
            <AlertTitle className="text-gray-800">Recommended: validate with Randmar Assistant</AlertTitle>
            <AlertDescription className="space-y-3 text-[15px] text-gray-700">
              <p>
                Ask the Assistant to generate an <span className="font-medium">.xlsx</span> template, map your columns to Randmar fields,
                and spot missing mandatory values before you email your spreadsheet.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => sendMessageWithClear(bulkUploadAssistantPrompt)}
                className="h-10 gap-2 rounded-xl"
              >
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Open Assistant
              </Button>
            </AlertDescription>
          </Alert>
          <p className="text-[15px] text-gray-600">
            Include these mandatory values in your <span className="font-medium">.xlsx</span> file so we can successfully insert every product:
          </p>
          <div className="flex flex-wrap gap-2">
            {BULK_UPLOAD_MANDATORY_FIELDS.map((field) => (
              <Badge key={field} variant="secondary" size="sm">
                {field}
              </Badge>
            ))}
          </div>
          <Separator />
          <p className="text-[15px] text-gray-600">
            Email the completed spreadsheet as a <span className="font-medium">.xlsx</span> attachment to{" "}
            <a className="font-semibold text-[color:var(--ops-accent)] hover:underline" href="mailto:echo@randmar.io">
              echo@randmar.io
            </a>{" "}
            and our team will handle the bulk upload.
          </p>
        </CardContent>
      </Card>

      <Dialog
        open={confirmInsertOpen}
        onOpenChange={(open) => {
          if (!busy) setConfirmInsertOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Insert {mpn || "this product"}?</DialogTitle>
            <DialogDescription>
              Randmar will create a new SKU for <span className="font-medium">{title || "your product"}</span>. You can edit details after insertion.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button type="button" variant="outline" onClick={() => setConfirmInsertOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirmInsert} disabled={busy || !distributionIsValid}>
              {isInsertBusy ? "Inserting..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={existingProductDialogOpen}
        onOpenChange={(open) => {
          if (!busy) setExistingProductDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Product already exists</DialogTitle>
            <DialogDescription>
              This UPC is already in Randmar. You can open the existing product or go back and enter a different UPC.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button type="button" variant="outline" onClick={() => setExistingProductDialogOpen(false)} disabled={busy}>
              Use a different UPC
            </Button>
            <Button type="button" onClick={handleExistingProductNavigate} disabled={busy || !existingProduct?.RandmarSKU}>
              Open product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default withRoot(InsertProductPage);
